import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, Star } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Eyebrow } from "@/components/public/Motion";

const LABELS = { 5: "Excellent", 4: "Good", 3: "OK", 2: "Poor", 1: "Very poor" };

export default function Feedback() {
  const { token } = useParams();
  const [qs] = useSearchParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [rid, setRid] = useState(() => localStorage.getItem(`fb-${token}`) || null);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const first = useRef(true);
  const submit = async (r, c) => {
    if (busy) return false;
    setBusy(true);
    try {
      const id = rid || localStorage.getItem(`fb-${token}`);
      const { data } = await api.post(`/public/feedback/${token}`, { rating: r, response_id: id, ...(c !== undefined ? { comment: c } : {}) });
      setRid(data.response_id); localStorage.setItem(`fb-${token}`, data.response_id); setRating(r); setErr("");
      return true;
    } catch (e) { setErr(errMsg(e)); return false; } finally { setBusy(false); }
  };
  useEffect(() => {
    api.get(`/public/feedback/${token}`).then((r) => {
      setInfo(r.data);
      const r0 = parseInt(qs.get("r") || "0", 10);
      if (first.current && r0 >= 1 && r0 <= 5) { first.current = false; submit(r0); }
    }).catch((e) => setErr(errMsg(e)));
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="mx-auto max-w-2xl px-5 pb-24 pt-32" data-testid="feedback-page">
      <Eyebrow>Hospital feedback</Eyebrow>
      <h1 className="mt-3 text-4xl font-black tracking-tight sm:text-5xl">How did we do?</h1>
      {info && <p className="mt-3 text-white/75" data-testid="feedback-context">{info.hospital} · {info.month}</p>}
      {err && <p className="mt-6 rounded-xl bg-red-100 p-3 text-sm text-red-700" data-testid="feedback-error">{err}</p>}
      {info && (
        <div className="mt-8 space-y-6 rounded-3xl bg-white p-6 text-black sm:p-8">
          {rating > 0 && <p className="flex items-center gap-2 font-bold text-emerald-700" data-testid="feedback-thanks"><CheckCircle2 className="h-6 w-6" /> Thank you - you rated us {rating}/5 ({LABELS[rating]})</p>}
          <div className="flex flex-wrap gap-2" data-testid="feedback-stars">{[1, 2, 3, 4, 5].map((n) => (
            <button key={n} onClick={() => submit(n)} disabled={busy} aria-label={`${n} stars`} data-testid={`feedback-star-${n}`} className="rounded-full p-1 transition-transform active:scale-90">
              <Star className={`h-11 w-11 ${n <= rating ? "fill-[#FEDC19] text-[#d4b400]" : "text-black/25"}`} /></button>
          ))}</div>
          {rating > 0 && (sent ? <p className="font-semibold" data-testid="feedback-comment-sent">Comment received - thank you for helping us improve.</p> : (
            <div className="space-y-3"><label className="block text-sm font-bold">Anything we could do better? (optional)
              <textarea rows={4} maxLength={1000} value={comment} onChange={(e) => setComment(e.target.value)} className="mt-1 w-full rounded-xl border border-black/20 p-3" data-testid="feedback-comment" /></label>
              <button onClick={async () => { if (await submit(rating, comment)) setSent(true); }} disabled={!comment.trim()} className="min-h-12 rounded-full bg-[#DB0000] px-6 font-bold text-white disabled:opacity-40" data-testid="feedback-comment-send">Send comment</button></div>
          ))}
          {rating === 0 && <p className="text-sm text-black/60">Tap a star to rate our service.</p>}
        </div>
      )}
    </div>
  );
}
